import React, { useState, useCallback, useEffect, useRef } from 'react';

import Button from '~c/components/base/button';
import { spc } from '~c/components/base/spacer';
import { nodeAuthApi } from '~c/services';

type OktaAuthJSType = typeof import('@okta/okta-auth-js').OktaAuth;

interface OktaLoginButtonProps {
  onError?: (error: string) => void;
  on2FARequired?: (idToken: string) => void;
  loading?: boolean;
  invitationToken?: string;
  /** Per-org Okta config from SSO discovery. If provided, skips fetching global /auth/okta/config. */
  oktaConfig?: {
    clientId: string;
    issuer: string;
    domain: string;
  };
}

// Simple Okta "O" logo as inline SVG
const OktaIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="12" r="10" fill="#007DC1" />
    <circle cx="12" cy="12" r="5" fill="white" />
  </svg>
);

export const OktaLoginButton: React.FC<OktaLoginButtonProps> = ({
  onError,
  on2FARequired,
  loading: externalLoading = false,
  invitationToken,
  oktaConfig: propConfig,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [fetchOktaConfig] = nodeAuthApi.useLazyOktaConfigQuery();
  const [oktaLogin] = nodeAuthApi.useOktaLoginMutation();
  const [oktaLoginWithInvitation] =
    nodeAuthApi.useOktaLoginWithInvitationMutation();

  const oktaAuthClassRef = useRef<OktaAuthJSType | null>(null);
  const [sdkLoaded, setSdkLoaded] = useState(false);

  // Lazy-load Okta Auth JS (browser-only, code-split)
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let cancelled = false;

    const loadOktaSdk = async () => {
      try {
        const oktaAuthJs = await import(
          /* webpackChunkName: "okta-auth-js" */
          '@okta/okta-auth-js'
        );

        if (cancelled) {
          return;
        }

        if (!oktaAuthJs.OktaAuth) {
          throw new Error('OktaAuth not found in okta-auth-js bundle');
        }

        oktaAuthClassRef.current = oktaAuthJs.OktaAuth;
        setSdkLoaded(true);
      } catch (err) {
        if (cancelled) {
          return;
        }

        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('[Okta] Failed to load:', err);
        }
        setSdkError(
          'Failed to load Okta authentication. Please refresh and try again.'
        );
      }
    };

    loadOktaSdk();

    return () => {
      cancelled = true;
    };
  }, []);

  const isSdkLoading = typeof window !== 'undefined' && !sdkLoaded && !sdkError;
  const isLoading = externalLoading || isProcessing || isSdkLoading;

  const handleOktaLogin = useCallback(async () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (sdkError) {
      onError?.(sdkError);
      return;
    }

    if (!oktaAuthClassRef.current) {
      onError?.('Okta authentication is still loading. Please try again.');
      return;
    }

    if (isProcessing) {
      return;
    }

    setIsProcessing(true);
    let navigating = false;

    try {
      // Use prop config (from SSO discovery) or fetch global config
      let configResult: { clientId: string; issuer: string };
      if (propConfig?.clientId && propConfig?.issuer) {
        configResult = propConfig;
      } else {
        configResult = await fetchOktaConfig().unwrap();
      }
      if (!configResult.clientId || !configResult.issuer) {
        throw new Error('Okta auth not configured');
      }

      const OktaAuth = oktaAuthClassRef.current;
      const oktaClient = new OktaAuth({
        issuer: configResult.issuer,
        clientId: configResult.clientId,
        redirectUri: window.location.origin + '/login/callback',
        scopes: ['openid', 'profile', 'email'],
        pkce: true,
      });

      // Use popup-based token flow
      const tokenResponse = await oktaClient.token.getWithPopup({
        scopes: ['openid', 'profile', 'email'],
        prompt: 'login',
      });

      const idToken = tokenResponse.tokens?.idToken?.idToken;

      if (!idToken) {
        throw new Error('No ID token received from Okta');
      }

      const result = invitationToken
        ? await oktaLoginWithInvitation({
            idToken,
            invitationToken,
          })
        : await oktaLogin({
            idToken,
          });

      if ('error' in result) {
        const errorResponse = result.error as {
          data?: { statusCode?: number; message?: string; error?: string };
          status?: number;
        };

        // eslint-disable-next-line no-console
        console.error(
          '[Okta] Backend /okta/token returned error:',
          errorResponse
        );

        const status = errorResponse.status;
        let errorMessage: string;

        if (!status) {
          errorMessage =
            'Unable to reach the server. Please check your internet connection and try again.';
        } else if (status === 401) {
          errorMessage =
            errorResponse.data?.message ??
            'Okta authentication failed. Please try again.';
        } else if (status === 400) {
          errorMessage =
            errorResponse.data?.message ??
            'Invalid authentication request. Please try again.';
        } else if (status === 403) {
          errorMessage =
            errorResponse.data?.message ??
            'Your organization is not registered on this platform.';
        } else if (status === 429) {
          errorMessage =
            'Too many login attempts. Please wait a moment and try again.';
        } else if (status >= 500) {
          errorMessage =
            'Okta authentication failed due to a server error. Please try again later.';
        } else {
          errorMessage =
            errorResponse.data?.message ??
            'Okta authentication failed. Please try again.';
        }

        onError?.(errorMessage);
        return;
      }

      const data = result.data;

      // Check 2FA before success flag
      if (
        data?.requires2FA ||
        (data?.success === false && data?.user?.otpEnabled)
      ) {
        if (on2FARequired) {
          on2FARequired(idToken);
        } else {
          onError?.('Two-factor authentication is required.');
        }
        return;
      }

      if (!data || data.success === false) {
        const errorMessage = data?.message ?? 'Account verification required';
        onError?.(errorMessage);
        return;
      }

      // Full page nav to refresh CSRF token
      navigating = true;

      if (invitationToken) {
        window.location.href = `/accept-invitation?token=${encodeURIComponent(invitationToken)}`;
        return;
      }

      const params = new URLSearchParams(window.location.search);
      const invitation = params.get('invitation');

      if (invitation) {
        window.location.href = `/accept-invitation?token=${invitation}`;
      } else {
        window.location.href = '/select-organization';
      }
    } catch (error: any) {
      // User closed popup
      if (
        error.name === 'AuthApiError' ||
        error.errorCode === 'login_cancelled' ||
        error.message?.includes('User closed the popup') ||
        error.message?.includes('Popup closed')
      ) {
        return;
      }

      const errorMessage =
        error.message || 'Okta sign-in failed. Please try again.';
      onError?.(errorMessage);
    } finally {
      if (!navigating) {
        setIsProcessing(false);
      }
    }
  }, [
    isProcessing,
    sdkError,
    onError,
    on2FARequired,
    fetchOktaConfig,
    oktaLogin,
    oktaLoginWithInvitation,
    invitationToken,
  ]);

  if (typeof window === 'undefined') {
    return null;
  }

  return (
    <Button
      onClick={handleOktaLogin}
      loading={isLoading}
      fullWidth
      variant="outlined"
      sx={{ mt: spc.xs, mb: spc.between_vertical_s }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <OktaIcon />
        <div>Continue with Okta</div>
      </div>
    </Button>
  );
};

export default OktaLoginButton;
